/**
 * Rest Day Card
 * 
 * Renders a minimal rest/recovery card using WorkoutCardShell
 * for visual consistency with workout cards.
 * Dashed border, muted tones, shield icon.
 */

import { Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WorkoutCardShell } from './WorkoutCardShell';

interface RestDayCardProps {
  /** compact = week grid, standard = pulse timeline */
  density?: 'compact' | 'standard';
  className?: string;
  onClick?: () => void;
}

export function RestDayCard({
  density = 'compact',
  className,
  onClick,
}: RestDayCardProps) {
  const isCompact = density === 'compact';

  return (
    <div
      className={cn(
        'relative',
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
      style={{
        // Override border to dashed via wrapper
      }}
    >
      <WorkoutCardShell
        role="ambient"
        intent="rest"
        status="planned"
        className="border-dashed !border-white/[0.08]"
        padding={isCompact ? 'p-2.5' : 'p-3'}
      >
        <div className={cn(
          'flex items-center',
          isCompact ? 'gap-2' : 'gap-2.5',
        )}>
          <Shield
            className={cn(
              'flex-shrink-0 text-muted-foreground/50',
              isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4',
            )}
            strokeWidth={1.5}
          />
          <div className="min-w-0">
            <p className={cn(
              'font-mono uppercase tracking-wider text-muted-foreground/60',
              isCompact ? 'text-[9px]' : 'text-[10px]',
            )}>
              Recovery
            </p>
            {!isCompact && (
              <p className="text-[11px] font-mono text-muted-foreground/40 mt-0.5">
                No load prescribed
              </p>
            )}
          </div>
        </div>
      </WorkoutCardShell>
    </div>
  );
}
